
import store from '@/store';
import { resolveMediaUrl } from './mediaUrl';

export const handleCopy = (text) => {
  try {
    let str = decodeHtmlEntities(text);
    
    // 优先使用现代 Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(str)
        .then(() => {
          // 复制成功
        })
        .catch((err) => {
          console.warn('Clipboard API failed, trying fallback:', err);
          fallbackCopyTextToClipboard(str);
        });
    } else {
      // 降级处理
      fallbackCopyTextToClipboard(str);
    }
  } catch (error) {
    console.error('Error in handleCopy:', error);
    // 最后的降级处理
    fallbackCopyTextToClipboard(text);
  }
};

// 降级复制方法（兼容Mac和旧浏览器）
function fallbackCopyTextToClipboard(text) {
  try {
    // 创建临时文本区域
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // 设置样式，使其不可见
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    
    // 添加到DOM并选中
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    // 尝试复制
    let successful = false;
    try {
      successful = document.execCommand('copy');
    } catch (err) {
      console.error('execCommand copy failed:', err);
    }
    
    // 清理
    document.body.removeChild(textArea);
    
    if (!successful) {
      console.error('All copy methods failed');
      // 可以在这里添加用户提示，比如显示文本让用户手动复制
    }
  } catch (error) {
    console.error('Fallback copy failed:', error);
  }
}

export function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function() {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if ((Date.now() - lastRan) >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

/**
 * 将纯文本转为可安全用于 v-html 的内容。
 * 先解码历史消息中的 HTML 实体，再统一转义，避免：
 * 1) 用户输入的 <xxx 被当成标签截断后续内容
 * 2) 服务端已转义的 &lt; 被二次转义成字面量 &lt;
 * 必须在插入链接/表情 HTML 之前调用。
 */
export function parseTextToHtml(text) {
  if (text === undefined || text === null) {
    return '';
  }
  const decoded = decodeHtmlEntities(String(text));
  return decoded
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * 解码常见 HTML 实体（复制到剪贴板，或 parseTextToHtml 预处理）
 * 仅匹配完整实体，避免误伤正文中的 amp;/quot; 等子串
 */
export function decodeHtmlEntities(text) {
  if (text === undefined || text === null) {
    return '';
  }
  let str = String(text);
  // 先处理双重编码，再处理单层实体
  str = str.replace(/&amp;lt;/gi, '<');
  str = str.replace(/&amp;gt;/gi, '>');
  str = str.replace(/&amp;nbsp;/gi, ' ');
  str = str.replace(/&amp;quot;/gi, '"');
  str = str.replace(/&amp;apos;/gi, "'");
  str = str.replace(/&amp;ldquo;/gi, '"');
  str = str.replace(/&amp;rdquo;/gi, '"');
  str = str.replace(/&amp;/gi, '&');
  str = str.replace(/&lt;/gi, '<');
  str = str.replace(/&gt;/gi, '>');
  str = str.replace(/&nbsp;/gi, ' ');
  str = str.replace(/&quot;/gi, '"');
  str = str.replace(/&apos;/gi, "'");
  str = str.replace(/&#39;/g, "'");
  return str;
}

export function htmlToPlainText(html) {
  if (html === undefined || html === null) {
    return '';
  }
  const str = String(html);
  if (!str) {
    return '';
  }
  if (str.indexOf('<') === -1) {
    return str.replace(/\u00A0/g, ' ').replace(/[\u200B\uFEFF]/g, '').trim();
  }
  const div = document.createElement('div');
  div.innerHTML = str;
  const walk = (node) => {
    let out = '';
    const children = node.childNodes || [];
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.nodeType === 3) {
        out += child.textContent || '';
        continue;
      }
      if (child.nodeType !== 1) {
        continue;
      }
      const name = child.nodeName;
      if (name === 'IMG') {
        out += child.getAttribute('data-emoji-code') || child.getAttribute('alt') || '';
        continue;
      }
      if (name === 'BR') {
        out += '\n';
        continue;
      }
      if (name === 'DIV' || name === 'P' || name === 'LI') {
        if (out && !out.endsWith('\n')) {
          out += '\n';
        }
        out += walk(child);
        continue;
      }
      out += walk(child);
    }
    return out;
  };
  return walk(div).replace(/\u00A0/g, ' ').replace(/[\u200B\uFEFF]/g, '').replace(/\s+\n/g, '\n').trim();
}

/**
 * 文本消息富文本渲染：转义 → 链接 → 表情
 * 供气泡/引用/会话摘要等统一使用，避免遗漏转义
 */
export function formatMessageHtml(content, linkColor = '', emojiClass = 'emoji-normal') {
  // 延迟引用，避免与部分打包路径下的循环依赖问题
  const urlApi = require('@/api/url.js').default;
  const emotion = require('@/api/emotion.js').default;
  let text = parseTextToHtml(content);
  text = urlApi.replaceURLWithHTMLLinks(text, linkColor);
  text = emotion.transform(text, emojiClass);
  if (emotion.wrapFlagEmojisHtml) {
    text = emotion.wrapFlagEmojisHtml(text);
  }
  return text.trim();
}

/**
 * 获取带用户ID的localStorage key
 * @param {string} key 原始key
 * @returns {string} 带用户ID的key
 */
export function getUserKey(key, id) {
  return `${key}_${id}`;
}

/** 应用内复制媒体后的粘贴缓存（剪贴板 API 不可用时的降级） */
let clipboardMediaFilesCache = null;

export function setClipboardMediaFilesCache(files) {
  clipboardMediaFilesCache = files;
}

export function consumeClipboardMediaFilesCache() {
  const files = clipboardMediaFilesCache;
  clipboardMediaFilesCache = null;
  return files;
}

function fileNameFromUrl(url, defaultName) {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const base = pathname.split('/').pop();
    if (base && base.includes('.')) {
      return decodeURIComponent(base);
    }
  } catch (e) { /* ignore */ }
  return defaultName;
}

function guessMimeFromFileName(fileName) {
  const lower = (fileName || '').toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.mp4')) return 'video/mp4';
  if (lower.endsWith('.webm')) return 'video/webm';
  if (lower.endsWith('.ogg')) return 'video/ogg';
  if (lower.endsWith('.pdf')) return 'application/pdf';
  return 'application/octet-stream';
}

function toCdnProxyUrl(url) {
  if (!url) {
    return '';
  }
  try {
    const remote = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    const s3 = store.state.configStore.s3Url;
    if (!s3) {
      return '';
    }
    const base = new URL(s3.includes('://') ? s3 : `https://${s3}`);
    if (remote.host !== base.host) {
      return '';
    }
    return `/cdn-proxy${remote.pathname}${remote.search}`;
  } catch (e) {
    return '';
  }
}

function fetchBlobViaImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const mime = guessMimeFromFileName(url);
        const type = mime && mime.indexOf('image/') === 0 ? mime : 'image/jpeg';
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('canvas toBlob failed'));
            return;
          }
          resolve(blob);
        }, type);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
}

async function fetchBlobDirect(url) {
  const response = await fetch(url, {
    mode: 'cors',
    credentials: 'omit'
  });
  if (!response.ok) {
    throw new Error(`fetch failed: ${response.status}`);
  }
  return response.blob();
}

export async function fetchUrlAsBlob(url) {
  const resolvedUrl = resolveMediaUrl(url, store.state.configStore.s3Url);
  if (!resolvedUrl) {
    throw new Error('invalid media url');
  }
  try {
    return await fetchBlobDirect(resolvedUrl);
  } catch (directErr) {
    const proxyUrl = toCdnProxyUrl(resolvedUrl);
    if (proxyUrl) {
      try {
        return await fetchBlobDirect(proxyUrl);
      } catch (e) { /* continue */ }
    }
    const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(resolvedUrl);
    if (isImage) {
      try {
        return await fetchBlobViaImage(resolvedUrl);
      } catch (e) { /* continue */ }
    }
    throw directErr;
  }
}

function blobToFile(blob, fileName) {
  const type = (blob.type && blob.type !== 'application/octet-stream')
    ? blob.type
    : guessMimeFromFileName(fileName);
  return new File([blob], fileName, { type });
}

async function writeFileToSystemClipboard(file) {
  if (!navigator.clipboard || !window.ClipboardItem) {
    throw new Error('Clipboard file API not supported');
  }
  const type = file.type || 'application/octet-stream';
  await navigator.clipboard.write([
    new window.ClipboardItem({
      [type]: Promise.resolve(file)
    })
  ]);
}

function parseMessageMedia(msgInfo, MESSAGE_TYPE) {
  if (
    msgInfo.type !== MESSAGE_TYPE.IMAGE &&
    msgInfo.type !== MESSAGE_TYPE.FILE &&
    msgInfo.type !== MESSAGE_TYPE.VIDEO
  ) {
    return null;
  }
  const content = JSON.parse(msgInfo.content);
  const s3Url = store.state.configStore.s3Url;
  if (msgInfo.type === MESSAGE_TYPE.IMAGE) {
    const url = resolveMediaUrl(content.originUrl || content.thumbUrl, s3Url);
    return {
      url,
      fileName: fileNameFromUrl(url, 'image.png')
    };
  }
  if (msgInfo.type === MESSAGE_TYPE.FILE) {
    return {
      url: resolveMediaUrl(content.url, s3Url),
      fileName: content.name || fileNameFromUrl(content.url, 'file')
    };
  }
  if (msgInfo.type === MESSAGE_TYPE.VIDEO) {
    const url = resolveMediaUrl(content.videoUrl, s3Url);
    return {
      url,
      fileName: fileNameFromUrl(url, 'video.mp4')
    };
  }
  return null;
}

function getMessageTextForCopy(msgInfo, MESSAGE_TYPE) {
  if (msgInfo.type === MESSAGE_TYPE.USER_CARD) {
    return String(JSON.parse(msgInfo.content).userId);
  }
  if (msgInfo.type === MESSAGE_TYPE.GROUP_CARD) {
    return String(JSON.parse(msgInfo.content).groupId);
  }
  return msgInfo.content;
}

/**
 * 复制消息：文本走剪贴板文本；图片/视频/文件复制为 File，可在聊天输入框粘贴发送
 * @returns {Promise<{ kind: 'text' | 'file' }>}
 */
export async function copyMessageContent(msgInfo, MESSAGE_TYPE) {
  const media = parseMessageMedia(msgInfo, MESSAGE_TYPE);
  if (media) {
    if (!media.url) {
      throw new Error('invalid media url');
    }
    const blob = await fetchUrlAsBlob(media.url);
    const file = blobToFile(blob, media.fileName);
    setClipboardMediaFilesCache([file]);
    try {
      await writeFileToSystemClipboard(file);
    } catch (err) {
      console.warn('write file to clipboard failed, in-app paste cache kept:', err);
    }
    return { kind: 'file' };
  }
  setClipboardMediaFilesCache(null);
  const text = getMessageTextForCopy(msgInfo, MESSAGE_TYPE);
  handleCopy(text);
  return { kind: 'text' };
}